import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkSlug from 'remark-slug';
import rehypeExternalLinks from 'rehype-external-links';
import remarkFrontmatter from 'remark-frontmatter';
import { Progress } from '@backstage/core-components';

export const HelpPageRender = ({ pageName }: any) => {
  const file_name = `${pageName}.md`;
  const [post, setPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadHelpContent = async () => {
      if (!pageName) {
        if (isMounted) {
          setPost('No help content is configured for this page.');
          setLoading(false);
        }
        return;
      }

      try {
        const markdownAsset = await import(
          `../../helpMarkdownFile/${file_name}`
        );
        const response = await fetch(markdownAsset.default, {
          cache: 'no-store',
        });

        if (!response.ok && response.status !== 304) {
          throw new Error(`Failed to load help content: ${response.status}`);
        }

        const data = await response.text();

        // Some environments return app index.html for missing assets.
        const isHtmlFallback =
          data.trim().toLowerCase().startsWith('<!doctype html') ||
          data.trim().toLowerCase().startsWith('<html');

        if (isHtmlFallback || !data.trim()) {
          throw new Error('Help markdown asset not found in this environment');
        }

        if (isMounted) {
          setPost(data);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (isMounted) {
          setPost('No help content is available for this page right now.');
          setError(
            `Unable to load ${file_name}. Help content is not configured for this page.`,
          );
          setLoading(false);
        }
      }
    };

    setLoading(true);
    loadHelpContent();

    return () => {
      isMounted = false;
    };
  }, [file_name, pageName]);

  if (loading) return <Progress />;

  if (error) {
    return <p>{error}</p>;
  }

  return (
    <ReactMarkdown
      children={post}
      linkTarget="_blank"
      remarkPlugins={[remarkGfm, remarkSlug, remarkFrontmatter]}
      rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeExternalLinks]}
    />
  );
};

export default HelpPageRender;
